import * as Hooks from 'preact/hooks';
import * as Selectors from '../../store/selectors';
import { useDispatch, useSelector } from './redux';
import { pipe } from 'fp-ts/function';
import * as E from 'fp-ts/Either';
import * as ConfigPropStore from '../../store/configurableProperties';
import { ConfigurableProperties } from '@dora/backpack';
import { logWarning } from '../../store/actions/logging';
import { NonEmptyArray } from 'fp-ts/lib/NonEmptyArray';
import { append } from 'fp-ts/lib/Array';

export const useGetGlobalConfigurableProperties = () => {
    const dispatch = useDispatch();

    const products = useSelector(Selectors.Catalogue.productsInCatalogueEntriesForCurrentRange);

    Hooks.useEffect(() => {
        pipe(
            E.Do,
            E.bind('products', () => products),
            E.bind('globalConfigurableProps', ({ products }) => {
                return E.right(
                    products
                        .filter(p => p.configurableProperties !== undefined)
                        .map(({ configurableProperties: configurableProperties, properties }) => {
                            return Object.fromEntries(
                                Object.entries(
                                    configurableProperties as ConfigurableProperties.Gift,
                                )
                                    .filter(([propertyKey, { isGlobal, label, type }]) => {
                                        const value = properties[propertyKey];
                                        if (value === undefined) {
                                            dispatch(
                                                logWarning(
                                                    `Gift product configurableProperty found which has no entry in properties, ignoring`,
                                                    {
                                                        propertyKey,
                                                        allConfigurableProperties:
                                                            configurableProperties,
                                                        allProperties: properties,
                                                    },
                                                ),
                                            );
                                        }

                                        const isValueFinish =
                                            ConfigurableProperties.isFinish(value);
                                        if (!isValueFinish) {
                                            dispatch(
                                                logWarning(
                                                    `Gift product configurableProperty found that isn't a finish, ignoring`,
                                                    {
                                                        propertyKey,
                                                        propertyValue: value,
                                                        allConfigurableProperties:
                                                            configurableProperties,
                                                        allProperties: properties,
                                                    },
                                                ),
                                            );
                                        }
                                        return value !== undefined || isValueFinish;
                                    })
                                    .map(([propertyKey, { isGlobal, label, type }]) => {
                                        const value = properties[propertyKey];
                                        return [
                                            propertyKey,
                                            {
                                                label,
                                                // filter above ensures this is the case
                                                value: value as ConfigurableProperties.FinishT,
                                            },
                                        ];
                                    }),
                            );
                        })
                        .reduce((props, prod) => {
                            Object.entries(prod).forEach(([propertyKey, { label, value }]) => {
                                if (props[propertyKey]) {
                                    // only add the option if it isn't in there already
                                    if (
                                        !props[propertyKey].options.find(o =>
                                            ConfigurableProperties.finishesEqual(o, value),
                                        )
                                    ) {
                                        props[propertyKey].options = append(value)(
                                            props[propertyKey].options,
                                        );
                                    }
                                } else {
                                    props[propertyKey] = { label, options: [value] };
                                }
                            });
                            return props;
                        }, {} as { [propertyKey: string]: { label: string; options: NonEmptyArray<ConfigurableProperties.FinishT> } }),
                );
            }),
            E.map(({ globalConfigurableProps }) => {
                dispatch(
                    ConfigPropStore.setConfigurableProperties(
                        Object.entries(globalConfigurableProps).map(
                            ([propertyKey, { label, options }]) => ({
                                propertyKey,
                                label,
                                options,
                            }),
                        ),
                    ),
                );
            }),
        );
    }, [products]);
};
